import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import type { CompanyContact } from '@/api/v4/organization-contacts.api';
import type { RenameByT } from '@/helpers/other';
import type {
  IEntityType,
  OrganizationLocation,
  OrganizationLocationResponse,
} from '@/types';
import type { IPageState } from '@/ui/table/table.types';

import { MAX_VISIBLE_CONTACTS_NUMBER } from './contacts/use-company-contacts';
import type { CreatedCompany } from '@/api/v4/organization.api';

export type ICompanyListedResponse = {
  id: string;
  name: string;
  domain: string | null;
  logo_url?: string;
  locations: OrganizationLocationResponse[];
  average_rating: string | null;
  from_cb?: boolean;
  listings_count: number;
  year_established: number | null;
  cb_permalink?: string | null;
};

export interface PoweredByAPI {
  powered_by: string;
  last_edited_at: string;
}

export interface PoweredValue<T> extends PoweredByAPI {
  value: T;
}

interface IPoweredBy extends Camelize<PoweredByAPI> {
  id: string;
  type: IEntityType;
}

type IEmployeesRange = IPoweredBy & {
  rangeValues: string;
};

type IOwnershipStatus = IPoweredBy & {
  name: string;
};

type IActivityStatus = IPoweredBy & {
  name: CompanyActivityStatus;
};

export interface EnrichedFromCrunchbaseCompanyData {
  name: Camelize<PoweredValue<string>>;
  activityStatus?: IActivityStatus;
  logoUrl: Camelize<PoweredValue<string | null>>;
  hqLocation: Camelize<PoweredValue<OrganizationLocation | null>>;
  linkedinUrl: Camelize<PoweredValue<string | null>>;
  website: Camelize<PoweredValue<string | null>>;
  ownershipStatus?: IOwnershipStatus;
  ticker: Camelize<PoweredValue<string | null>>;
  yearEstablished: Camelize<PoweredValue<number>>;
  employeesRange?: IEmployeesRange;
  keyOffering: Camelize<PoweredValue<string | null>>;
  ownedByIrm: Camelize<PoweredValue<string | null>>;
  detailedDescription: Camelize<PoweredValue<string>>;
  lastFundingDate: Camelize<PoweredValue<string | null>>;
  capitalRaised: Camelize<PoweredValue<string | null>>;
  stockExchangeSymbol?: Camelize<PoweredValue<string | null>>;
  fundingStage: Camelize<PoweredValue<string | null>>;
  tags: {
    poweredBy?: string;
  };
}

export type ICompanyListed = RenameByT<
  {
    average_rating: 'averageRating';
    listings_count: 'listingsCount';
    logo_url: 'logoUrl';
    from_cb: 'fromCb';
    year_established: 'yearEstablished';
    cb_permalink: 'cbPermalink';
  },
  ICompanyListedResponse
>;

export const companyIdState = atom<string | null>(null);

export const addCompanyInProgressState = atom<boolean>(false);

export const companiesSearchPhraseState = atom<string>('');

export const companiesAddedState = atom<CreatedCompany[]>([]);

export enum CompanyRatingType {
  Technology = 'technology',
  Team = 'team',
  Financial = 'financial',
}

export enum CompanyActivityStatus {
  ACTIVE = 'Active',
  CLOSED = 'Closed',
  STEALTH = 'Stealth',
}

export enum OwnershipStatus {
  PRIVATE = 'Private',
  PUBLIC = 'Public',
}

export const companyContactsPageState = atomWithReset<IPageState>({
  page: 1,
  pageSize: 50,
});

export const companyContactsAtomState = atomWithReset<CompanyContact[]>([]);

export const firstSixCompanyContactsAtomState = atomWithReset<CompanyContact[]>(
  [],
);

export const updateFirstSixCompanyContactsAtom = atom(null, (get, set) => {
  const contacts = get(companyContactsAtomState);
  set(firstSixCompanyContactsAtomState, contacts.slice(0, 6));
});

export const companyContactsAddCounterState = atom<number>(0);

export const companyContactsEditModeState = atomWithReset<boolean>(false);

export const isMoreContactsThanMaxVisibleAtom = atom(get => {
  const contacts = get(companyContactsAtomState);
  return contacts.length > MAX_VISIBLE_CONTACTS_NUMBER;
});

export const companyContactsForceSubmitState = atomWithReset<boolean>(false);

export const companyContactDeletedIdState = atom<string | null>(null);
